import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Moon } from 'lucide-react';
import { ARCHETYPES, DURATION_OPTIONS } from './archetypeData';

const AudioSettings = ({ archetypeId, musicTrack, onComplete, onBack }) => {
  const archetype = ARCHETYPES[archetypeId];
  const [duration, setDuration] = useState(180); // Default 3 min
  const [voiceVolume, setVoiceVolume] = useState(70);
  const [musicVolume, setMusicVolume] = useState(30);
  const [deepLayerMode, setDeepLayerMode] = useState(false);

  const handleCreate = () => {
    onComplete({
      duration,
      voiceVolume: voiceVolume / 100,
      musicVolume: musicVolume / 100,
      deepLayerMode
    });
  };

  return (
    <div className="fixed inset-0 bg-[#0a0a12] flex flex-col z-50">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-6 py-8">
        {/* Back button */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={onBack}
          className="flex items-center gap-2 text-white/40 hover:text-white/60 transition-colors mb-8"
        >
          <ChevronLeft size={20} />
          <span className="text-sm">Back</span>
        </motion.button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <h1 className="text-2xl font-light text-white mb-2">
            Create Your State
          </h1>
          <p className="text-white/40 text-sm">
            Finalize your reality
          </p>
        </motion.div>

        {/* Duration Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <h3 className="text-white/60 text-xs uppercase tracking-widest mb-3">Duration</h3>
          <div className="flex gap-3">
            {DURATION_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => setDuration(option.value)}
                className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all ${
                  duration === option.value
                    ? 'bg-purple-500/30 text-white border border-purple-500/50'
                    : 'bg-white/5 text-white/60 border border-transparent hover:bg-white/10'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Music Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8 p-4 rounded-xl bg-white/5 border border-white/10"
        >
          <h3 className="text-white/60 text-xs uppercase tracking-widest mb-2">Background Music</h3>
          <p className="text-white font-medium">{musicTrack?.name || 'Summer Breeze'}</p>
        </motion.div>

        {/* Volume Balance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <h3 className="text-white/60 text-xs uppercase tracking-widest mb-4">Volume Balance</h3>
          
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-white/60 text-sm">Voice</span>
                <span className="text-white/40 text-sm">{voiceVolume}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={voiceVolume}
                onChange={(e) => setVoiceVolume(Number(e.target.value))}
                className="w-full h-2 bg-white/10 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-purple-500 [&::-webkit-slider-thumb]:rounded-full"
              />
            </div>
            
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-white/60 text-sm">Music</span>
                <span className="text-white/40 text-sm">{musicVolume}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={musicVolume}
                onChange={(e) => setMusicVolume(Number(e.target.value))}
                className="w-full h-2 bg-white/10 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-purple-500 [&::-webkit-slider-thumb]:rounded-full"
              />
            </div>
          </div>
        </motion.div>

        {/* Deep Layer Mode Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <button
            onClick={() => setDeepLayerMode(!deepLayerMode)}
            className={`w-full p-4 rounded-xl flex items-center gap-4 transition-all ${
              deepLayerMode
                ? 'bg-indigo-500/20 border border-indigo-500/40'
                : 'bg-white/5 border border-transparent hover:bg-white/10'
            }`}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              deepLayerMode ? 'bg-indigo-500/30' : 'bg-white/10'
            }`}>
              <Moon size={18} className={deepLayerMode ? 'text-indigo-400' : 'text-white/60'} />
            </div>
            <div className="flex-1 text-left">
              <h4 className="text-white font-medium">Deep Layer Mode</h4>
              <p className="text-white/40 text-xs">Optimized for night listening</p>
            </div>
            <div className={`w-12 h-6 rounded-full relative transition-colors ${
              deepLayerMode ? 'bg-indigo-500' : 'bg-white/20'
            }`}>
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                deepLayerMode ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </div>
          </button>
        </motion.div>
      </div>

      {/* Fixed Create Button */}
      <div className="flex-shrink-0 p-6 pb-24 bg-gradient-to-t from-[#0a0a12] via-[#0a0a12] to-transparent">
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          onClick={handleCreate}
          className="w-full py-4 rounded-2xl bg-gradient-to-r from-purple-500 to-violet-500 text-white font-medium shadow-lg shadow-purple-500/30"
          data-testid="create-state-btn"
        >
          Create State
        </motion.button>
      </div>
    </div>
  );
};

export default AudioSettings;
